import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Home as HomeIcon, Package, Grid, ClipboardList, Users as UsersIcon, LogOut, ArrowLeft, RefreshCw, Trash2, Plus, Save, Send, CalendarClock, Settings as SettingsIcon, MessageSquare, MessageSquareQuote, Menu, FileDown, DatabaseZap, Sparkles, Wand2, Tag as TagIcon, FileText, HelpCircle, Lock, ShieldCheck, Truck, UserPlus, Store as StoreIcon, CreditCard, Ticket, EyeOff, Eye, AlertTriangle, LayoutTemplate } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { api, formatEUR, imageUrl, downloadAdminPdf, getBrowserTz } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import { AdminShipments, AdminShipmentDetail } from "./AdminEnvios";
import AdminSalidas from "./AdminSalidas";
import AdminAjustes from "./AdminAjustes";
import ImportarMercadonaPage from "./ImportarMercadona";
import AdminWhatsApp from "./AdminWhatsApp";
import AdminMarketing from "./AdminMarketing";
import AdminLegal from "./AdminLegal";
import AdminFaq from "./AdminFaq";
import AdminTestimonials from "./AdminTestimonials";
import AdminTransporters from "./AdminTransporters";
import AdminPaymentsSettings from "./AdminPaymentsSettings";
import AdminPromoCodes from "./AdminPromoCodes";
import AdminContent from "./AdminContent";
import AssignTransporterModal from "../../components/AssignTransporterModal";
import { Checkbox } from "../../components/ui/checkbox";
import StatusMessageModal from "../../components/StatusMessageModal";
import TranslateButton from "../../components/TranslateButton";
import ImageDropZone from "../../components/ImageDropZone";

/**
 * Phase 26 — "Emails enviados (7d)" tile.
 * Reads `GET /api/admin/emails/stats?days=7` and renders a count breakdown
 * by kind (confirmación, actualización, restablecer) so the admin can quickly
 * see transactional volume + failures without leaving the dashboard.
 */
const _EMAIL_KIND_LABEL = {
  order_confirmation: "Confirmación pedido",
  shipment_confirmation: "Confirmación envío",
  order_status_update: "Actualización pedido",
  shipment_status_update: "Actualización envío",
  password_reset: "Restablecer",
  other: "Otros",
};

function EmailStatsTile() {
  const [stats, setStats] = useState(null);
  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/emails/stats?days=7");
      setStats(data);
    } catch (_e) {
      setStats({ error: true });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!stats) {
    return <div className="h-32 rounded-2xl bg-neutral-100 animate-pulse" data-testid="admin-emails-tile-loading" />;
  }
  if (stats.error) {
    return null;
  }
  const failed = stats.by_status?.failed || 0;
  const noop = stats.by_status?.noop || 0;
  return (
    <div
      data-testid="admin-emails-tile"
      className="rounded-2xl border bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Emails enviados (7 días)</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Volumen transaccional por tipo</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black text-[#00953B]" data-testid="admin-emails-total">{stats.total}</div>
          {(failed > 0 || noop > 0) && (
            <div className="text-xs mt-1 space-x-2">
              {failed > 0 && (
                <span className="text-red-700 font-semibold" data-testid="admin-emails-failed">
                  {failed} fallidos
                </span>
              )}
              {noop > 0 && (
                <span className="text-amber-700" data-testid="admin-emails-noop">
                  {noop} sin enviar
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(_EMAIL_KIND_LABEL).map(([k, label]) => {
          const n = stats.by_kind?.[k] || 0;
          if (n === 0 && k === "other") return null;
          return (
            <div
              key={k}
              data-testid={`admin-emails-kind-${k}`}
              className="rounded-xl bg-neutral-50 border border-neutral-100 px-3 py-2"
            >
              <div className="text-[11px] text-neutral-500">{label}</div>
              <div className="text-lg font-black">{n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-16 text-center">Cargando…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

/**
 * Shared onClick helper for the "Descargar PDF pendientes" buttons across the
 * admin surface. `kind` = "envios" | "pedidos". When `aiSummary=true`, an
 * AI-generated executive paragraph is prepended (best-effort — the PDF still
 * downloads even if the AI call fails).
 */
async function downloadPendingPdf(kind, aiSummary = false) {
  const t = toast.loading(aiSummary ? "Generando PDF con IA…" : "Descargando…");
  try {
    const today = new Date().toISOString().slice(0, 10);
    const tzParam = `tz=${encodeURIComponent(getBrowserTz())}`;
    const qs = aiSummary ? `?ai_summary=1&${tzParam}` : `?${tzParam}`;
    const path =
      kind === "envios"
        ? `/admin/reports/shipments-pending.pdf${qs}`
        : `/admin/reports/orders-pending.pdf${qs}`;
    const filename =
      kind === "envios"
        ? `MercaGo-envios-pendientes${aiSummary ? "-ia" : ""}-${today}.pdf`
        : `MercaGo-pedidos-pendientes${aiSummary ? "-ia" : ""}-${today}.pdf`;
    await downloadAdminPdf(path, filename);
    toast.success("PDF descargado", { id: t });
  } catch (_e) {
    toast.error("No se pudo generar el PDF", { id: t });
  }
}

/**
 * Phase 21 — Manually re-run the idempotent startup migrations. Talks to
 * `POST /api/admin/migrations/run` (admin-only). Used to force-seed a fresh
 * production Mongo cluster without waiting for the next pod restart.
 */
function RunMigrationsButton() {
  const [running, setRunning] = useState(false);
  const onClick = async () => {
    if (running) return;
    setRunning(true);
    const t = toast.loading("Ejecutando migraciones…");
    try {
      const { data } = await api.post("/admin/migrations/run");
      const applied = (data.applied || []).length;
      const skipped = (data.skipped || []).length;
      const errors = (data.errors || []).length;
      const duration = data.duration_seconds ?? 0;
      if (errors > 0) {
        toast.error(
          `Migraciones completadas con ${errors} error(es) · aplicadas ${applied} · omitidas ${skipped} · ${duration}s`,
          { id: t, duration: 6000 },
        );
      } else {
        toast.success(
          `Migraciones OK · aplicadas ${applied} · omitidas ${skipped} · ${duration}s`,
          { id: t, duration: 5000 },
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "No se pudieron ejecutar las migraciones",
        { id: t },
      );
    } finally {
      setRunning(false);
    }
  };
  return (
    <Button
      data-testid="admin-run-migrations-btn"
      variant="outline"
      disabled={running}
      onClick={onClick}
      className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full disabled:opacity-60"
    >
      {running ? (
        <RefreshCw className="h-4 w-4 animate-spin" />
      ) : (
        <DatabaseZap className="h-4 w-4" />
      )}
      {running ? "Ejecutando…" : "Ejecutar migraciones"}
    </Button>
  );
}

/**
 * Phase 21 — IA "Resumen del día" tile on the admin dashboard. Calls
 * `GET /api/admin/ai/daily-digest` on mount, shows summary + bullets +
 * suggestions in Spanish. Includes a refresh button + last-updated caption.
 */
function AiDigestTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const url = refresh ? "/admin/ai/daily-digest?refresh=true" : "/admin/ai/daily-digest";
      const { data: d } = await api.get(url);
      setData(d);
      setGeneratedAt(d?.generated_at ? new Date(d.generated_at * 1000) : new Date());
    } catch (e) {
      toast.error("No se pudo cargar el resumen IA");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageLabel = () => {
    if (!generatedAt) return "";
    const secs = Math.max(0, Math.floor((Date.now() - generatedAt.getTime()) / 1000));
    if (secs < 60) return "hace unos segundos";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    return `hace ${hrs} h`;
  };

  return (
    <div
      data-testid="admin-ai-digest-tile"
      className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-5 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-full bg-emerald-500 text-white grid place-items-center">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-black text-lg leading-tight">IA — Resumen del día</div>
            <div className="text-xs text-neutral-500">
              {loading ? "Generando…" : `Última actualización ${ageLabel()}`}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          data-testid="admin-ai-digest-refresh-btn"
          disabled={loading}
          onClick={() => load(true)}
          className="gap-1 rounded-full"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
        </Button>
      </div>
      {data ? (
        <>
          <p data-testid="admin-ai-digest-summary" className="text-sm text-neutral-800">
            {data.summary}
          </p>
          {(data.bullets || []).length > 0 && (
            <ul data-testid="admin-ai-digest-bullets" className="text-sm space-y-1">
              {data.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#00953B]">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
          {(data.suggestions || []).length > 0 && (
            <div
              data-testid="admin-ai-digest-suggestions"
              className="rounded-xl bg-white border border-neutral-200 p-3 space-y-1"
            >
              <div className="text-xs font-semibold text-neutral-700">Sugerencias IA</div>
              {data.suggestions.map((s, i) => (
                <div key={i} className="text-sm text-neutral-800">→ {s}</div>
              ))}
            </div>
          )}
          {data.error && (
            <div className="text-xs text-amber-700">
              IA no disponible ({data.error}). Mostrando resumen mínimo.
            </div>
          )}
        </>
      ) : (
        <div className="h-24 rounded-xl bg-neutral-100 animate-pulse" />
      )}
    </div>
  );
}

function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = [
    { to: "/admin", label: "Dashboard", icon: HomeIcon, end: true },
    // Phase 35 — single "Productos" entry (was split into Envío/Local
    // during Phase 32; the Local storefront is hidden now).
    { to: "/admin/productos", label: "Productos", icon: Package, testid: "sidebar-productos" },
    { to: "/admin/categorias", label: "Categorías", icon: Grid },
    { to: "/admin/pedidos", label: "Pedidos", icon: ClipboardList },
    { to: "/admin/envios", label: "Envíos", icon: Send },
    { to: "/admin/salidas", label: "Salidas", icon: CalendarClock },
    { to: "/admin/usuarios", label: "Usuarios", icon: UsersIcon },
    { to: "/admin/marketing", label: "Marketing IA", icon: Sparkles },
    { to: "/admin/legal", label: "Legal", icon: FileText },
    { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
    { to: "/admin/testimonios", label: "Testimonios", icon: MessageSquareQuote },
    { to: "/admin/transportistas", label: "Transportistas", icon: Truck },
    { to: "/admin/settings/payments", label: "Métodos de pago", icon: CreditCard, testid: "sidebar-metodos-pago" },
    { to: "/admin/promo-codes", label: "Códigos promocionales", icon: Ticket, testid: "sidebar-promo-codes" },
    { to: "/admin/settings/content", label: "Contenido de páginas", icon: LayoutTemplate, testid: "sidebar-content-cms" },
    { to: "/admin/ajustes", label: "Ajustes", icon: SettingsIcon },
    { to: "/admin/importar-mercadona", label: "Importar Mercadona", icon: DatabaseZap, testid: "sidebar-importar-mercadona" },
    { to: "/admin/whatsapp", label: "WhatsApp", icon: MessageSquare },
  ];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  // Close sidebar on route change (mobile UX)
  React.useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);
  return (
    <div data-testid="admin-shell" className="min-h-screen bg-neutral-50 lg:grid lg:grid-cols-[240px_1fr]">
      {/* Mobile top-bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center gap-3 bg-neutral-900 text-white px-4 py-3">
        <button
          onClick={() => setSidebarOpen(true)}
          data-testid="admin-hamburger-btn"
          aria-label="Abrir menú de administración"
          className="h-9 w-9 grid place-items-center rounded-lg hover:bg-neutral-800"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/admin" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-[#00953B] text-white grid place-items-center font-black text-sm">M</div>
          <span className="font-black">MercaGo <span className="text-[#7fd39a]">Admin</span></span>
        </Link>
      </div>

      {/* Backdrop when drawer open */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        data-testid="admin-sidebar"
        className={`bg-neutral-900 text-neutral-100 p-5 flex flex-col z-50 transition-transform ${
          sidebarOpen
            ? "fixed inset-y-0 left-0 w-64 translate-x-0"
            : "fixed inset-y-0 left-0 w-64 -translate-x-full"
        } lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:w-auto lg:inset-auto lg:z-auto`}
      >
        <Link to="/admin" className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">M</div>
          <span className="text-xl font-black">MercaGo <span className="text-[#7fd39a]">Admin</span></span>
        </Link>
        <nav className="flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid || `admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  isActive ? "bg-[#00953B] text-white" : "hover:bg-neutral-800 text-neutral-300"
                }`
              }
            >
              <it.icon className="h-4 w-4" /> {it.label}
            </NavLink>
          ))}
          <Link to="/" className="mt-6 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-neutral-400 hover:bg-neutral-800">
            <ArrowLeft className="h-4 w-4" /> Volver a la tienda
          </Link>
        </nav>
        <div className="mt-auto text-xs text-neutral-400 pt-6 border-t border-neutral-800">
          <div className="font-semibold text-neutral-200">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
          <button
            data-testid="admin-logout-btn"
            onClick={() => { logout(); toast.success("Sesión cerrada"); navigate("/", { replace: true }); }}
            className="mt-3 flex items-center gap-2 text-neutral-300 hover:text-white"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="p-4 sm:p-8 max-w-6xl w-full min-w-0 overflow-x-hidden"><Outlet /></main>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [nextDep, setNextDep] = useState(null);
  const [waStats, setWaStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders?limit=10").then((r) => setOrders(r.data.items));
    api.get("/departures/next").then((r) =>
      setNextDep(r.data?.next === null ? null : r.data),
    ).catch(() => setNextDep(null));
    api.get("/admin/whatsapp-clicks/stats?days=7").then((r) =>
      setWaStats(r.data),
    ).catch(() => setWaStats(null));
  }, []);
  if (!stats) return <div className="h-64 bg-neutral-100 rounded-2xl animate-pulse" />;
  const cards = [
    { label: "Pedidos hoy", value: stats.orders_today, testid: "stat-orders-today" },
    { label: "Facturación hoy", value: formatEUR(stats.revenue_today_eur), testid: "stat-revenue-today" },
    { label: "Pedidos totales", value: stats.orders_total, testid: "stat-orders-total" },
    { label: "Productos", value: stats.products_total, testid: "stat-products-total" },
  ];
  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <h1 className="text-3xl font-black tracking-tight">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="rounded-2xl border bg-white p-5">
            <div className="text-xs text-neutral-500">{c.label}</div>
            <div className="mt-2 text-2xl font-black">{c.value}</div>
          </div>
        ))}
      </div>
      {/* Ops quick-links: bulk PDFs for the fulfillment team */}
      <div className="flex flex-wrap gap-3">
        <Button
          data-testid="admin-dashboard-envios-pdf-btn"
          variant="outline"
          onClick={() => downloadPendingPdf("envios")}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" /> Descargar envíos pendientes
        </Button>
        <Button
          data-testid="admin-dashboard-envios-pdf-ia-btn"
          variant="outline"
          onClick={() => downloadPendingPdf("envios", true)}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <Sparkles className="h-4 w-4" /> Envíos + resumen IA
        </Button>
        <Button
          data-testid="admin-dashboard-pedidos-pdf-btn"
          variant="outline"
          onClick={() => downloadPendingPdf("pedidos")}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" /> Descargar pedidos pendientes
        </Button>
        <Button
          data-testid="admin-dashboard-pedidos-pdf-ia-btn"
          variant="outline"
          onClick={() => downloadPendingPdf("pedidos", true)}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <Sparkles className="h-4 w-4" /> Pedidos + resumen IA
        </Button>
        <RunMigrationsButton />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NextDepartureTile dep={nextDep} />
        <WhatsAppClicksTile stats={waStats} />
      </div>
      <AiDigestTile />
      <EmailStatsTile />
      {stats.low_stock_count > 0 && (
        <div data-testid="admin-low-stock-alert" className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
          <b>{stats.low_stock_count}</b> productos sin stock — Revisar
        </div>
      )}
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="text-lg font-bold mb-3">Últimos pedidos</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500">
            <tr><th>Pedido</th><th>Cliente</th><th>Campamento</th><th>Total</th><th>Estado</th><th></th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t">
                <td className="py-2 font-mono">#{o.id.slice(0,8)}</td>
                <td>{o.customer_email}</td>
                <td>{o.camp}</td>
                <td className="font-semibold">{formatEUR(o.total)}</td>
                <td><Badge className="bg-emerald-100 text-emerald-800">{o.status}</Badge></td>
                <td><Link to={`/admin/pedidos/${o.id}`} className="text-[#00953B] font-semibold">Ver</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NextDepartureTile({ dep }) {
  const has = !!dep && !!dep.id;
  const valueBig = !has
    ? "—"
    : dep.days_remaining === 0
      ? "Hoy"
      : dep.days_remaining;
  const valueUnit =
    has && dep.days_remaining !== 0 && dep.days_remaining > 0 ? "días" : "";
  const subtitle = !has
    ? "Sin salidas programadas"
    : dep.destination_camp
      ? `Destino: ${dep.destination_camp}`
      : "Destino: todos los campamentos";
  return (
    <Link
      to="/admin/salidas"
      data-testid="stat-next-departure"
      className="rounded-2xl border bg-white p-5 hover:bg-neutral-50 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-neutral-500 flex items-center gap-1">
            <CalendarClock className="h-3 w-3" /> Próxima salida
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span data-testid="stat-next-departure-value" className="text-2xl font-black">
              {valueBig}
            </span>
            {valueUnit && (
              <span className="text-sm text-neutral-500">{valueUnit}</span>
            )}
          </div>
          <div data-testid="stat-next-departure-subtitle" className="text-xs text-neutral-500 mt-1">
            {subtitle}
          </div>
        </div>
      </div>
    </Link>
  );
}

const WA_SOURCE_LABELS = {
  fab: "fab",
  footer: "footer",
  order_page: "pedido",
  shipment_page: "envío",
  confirmation_action: "confirmación",
};

function WhatsAppClicksTile({ stats }) {
  const total = stats?.total ?? 0;
  const by = stats?.by_source || {};
  return (
    <Link
      to="/admin"
      data-testid="stat-whatsapp-clicks"
      className="rounded-2xl border bg-white p-5 hover:bg-neutral-50 transition-colors block"
    >
      <div className="text-xs text-neutral-500">Clicks WhatsApp (7 días)</div>
      <div className="mt-2 text-2xl font-black" data-testid="stat-whatsapp-clicks-total">
        {total}
      </div>
      <div
        data-testid="stat-whatsapp-clicks-by-source"
        className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-xs text-neutral-500"
      >
        {Object.entries(WA_SOURCE_LABELS).map(([k, label]) => (
          <span key={k}>
            <span className="font-semibold text-neutral-700">{label}</span>{" "}
            {by[k] ?? 0}
          </span>
        ))}
      </div>
    </Link>
  );
}

function AdminCatalogVisibilityCard() {
  // Fase 3 · Catalog kill-switch — tarjeta visible en la cabecera de la
  // pantalla /admin/productos. Un click ocultamos/mostramos todo el catálogo
  // (master switch reversible). Confirmación con Shadcn Dialog en 2 pasos.
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // "hide" | "show"

  const load = async () => {
    try {
      const { data } = await api.get("/admin/catalog/visibility");
      setState(data);
    } catch (_e) {
      /* pass */
    }
  };

  useEffect(() => { load(); }, []);

  const askConfirm = (action) => {
    setPendingAction(action);
    setConfirmOpen(true);
  };

  const runAction = async () => {
    if (!pendingAction) return;
    setBusy(true);
    try {
      const url = pendingAction === "hide"
        ? "/admin/catalog/hide-all"
        : "/admin/catalog/show-all";
      const { data } = await api.post(url);
      setState(data);
      toast.success(pendingAction === "hide"
        ? "Catálogo oculto. Los clientes ven un banner."
        : "Catálogo visible de nuevo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setPendingAction(null);
    }
  };

  const hidden = !!state?.catalog_hidden;
  const formatDT = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString("es-ES", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch (_e) { return iso; }
  };

  return (
    <div
      data-testid="admin-catalog-visibility-card"
      className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
        hidden ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"
      }`}
    >
      <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${
        hidden ? "bg-red-100" : "bg-emerald-100"
      }`}>
        {hidden ? <EyeOff className="h-6 w-6 text-red-700" /> : <Eye className="h-6 w-6 text-emerald-700" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            data-testid="admin-catalog-status-badge"
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
              hidden ? "bg-red-600 text-white" : "bg-emerald-600 text-white"
            }`}
          >
            {hidden ? "Catálogo oculto" : "Catálogo visible"}
          </span>
          {state?.affected_count > 0 && (
            <span className="text-xs text-neutral-500">
              · {state.affected_count} productos
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-neutral-700">
          {hidden ? (
            <>
              Los clientes ven un banner en <code>/envio</code> y <code>/local</code>. Ningún pedido se puede completar.
              {state?.hidden_at && (
                <div
                  data-testid="admin-catalog-hidden-since"
                  className="mt-1 text-xs text-neutral-500"
                >
                  Oculto desde <b>{formatDT(state.hidden_at)}</b>
                  {state?.hidden_by ? <> por <b>{state.hidden_by}</b></> : null}.
                </div>
              )}
            </>
          ) : (
            "El catálogo completo está disponible en la tienda para todos los clientes."
          )}
        </div>
      </div>
      <div className="shrink-0">
        {hidden ? (
          <Button
            data-testid="admin-catalog-show-btn"
            onClick={() => askConfirm("show")}
            disabled={busy}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Eye className="h-4 w-4 mr-1" /> Mostrar todo el catálogo
          </Button>
        ) : (
          <Button
            data-testid="admin-catalog-hide-btn"
            onClick={() => askConfirm("hide")}
            disabled={busy}
            className="rounded-full bg-red-600 hover:bg-red-700 text-white"
          >
            <EyeOff className="h-4 w-4 mr-1" /> Ocultar todo el catálogo
          </Button>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="admin-catalog-confirm-modal"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {pendingAction === "hide" ? "¿Ocultar todo el catálogo?" : "¿Mostrar todo el catálogo?"}
            </DialogTitle>
            <DialogDescription>
              {pendingAction === "hide"
                ? "Los clientes NO verán ningún producto ni podrán realizar pedidos en /envio ni /local hasta que lo actives de nuevo. Los carritos y pedidos ya iniciados se conservan."
                : "El catálogo volverá a ser visible para todos los clientes de forma inmediata."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={busy}
              data-testid="admin-catalog-confirm-cancel"
              className="rounded-full"
            >
              Cancelar
            </Button>
            <Button
              onClick={runAction}
              disabled={busy}
              data-testid="admin-catalog-confirm-yes"
              className={`rounded-full text-white ${
                pendingAction === "hide"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {busy
                ? "Aplicando…"
                : pendingAction === "hide"
                ? "Sí, ocultar catálogo"
                : "Sí, mostrar catálogo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function ProductList() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState("");
  const [detectingDupes, setDetectingDupes] = useState(false);
  const [dupeGroups, setDupeGroups] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const location = useLocation();
  // Phase 32 — shop filter (null = all, "envio" | "local") — persisted in the URL
  // via ?shop= so the admin can bookmark filtered views (e.g. the sidebar
  // "Productos Envío" link is just /admin/productos?shop=envio).
  const [searchParams, setSearchParams] = useSearchParams();
  const shopFilter = searchParams.get("shop") || "";  // "" | "envio" | "local"
  // Phase 32 — multi-select for bulk shop actions.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = () => {
    const params = new URLSearchParams();
    params.set("limit", "500");
    if (q) params.set("search", q);
    if (shopFilter) params.set("shop", shopFilter);
    return api.get(`/admin/products?${params.toString()}`).then((r) => setData(r.data));
  };
  // Phase 22 — refetch every time we land on the list route, so newly-created
  // or edited products appear without a manual refresh. Phase 32 — also on
  // shop-filter change so ?shop=envio/local re-queries the backend.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [location.key, shopFilter]);
  const setShopFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("shop", v);
    else next.delete("shop");
    setSelected(new Set());
    setSearchParams(next, { replace: true });
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (prev.size === data.items.length) return new Set();
      return new Set(data.items.map((p) => p.id));
    });
  };
  const bulkShop = async (shop, action) => {
    if (selected.size === 0) return;
    const verb = action === "add" ? "añadir" : "quitar";
    const shopLabel = shop === "envio" ? "Envío" : "Local";
    if (!window.confirm(`¿${verb.charAt(0).toUpperCase() + verb.slice(1)} ${selected.size} producto(s) ${action === "add" ? "a" : "de"} Tienda ${shopLabel}?`)) return;
    setBulkBusy(true);
    try {
      const { data: r } = await api.post("/admin/products/batch/shops", {
        product_ids: Array.from(selected),
        shop,
        action,
      });
      const orph = (r.would_be_orphaned || []).length;
      if (orph > 0) {
        toast.warning(`${r.modified} actualizado(s). ${orph} omitido(s) para no dejarlos sin tienda — añádelos a la otra tienda primero.`);
      } else {
        toast.success(`${r.modified} producto(s) actualizado(s)`);
      }
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setBulkBusy(false);
    }
  };
  const toggle = async (id, field, value) => {
    await api.patch(`/admin/products/${id}`, { [field]: value });
    load();
  };
  const del = async (p) => {
    if (!window.confirm(`¿Eliminar «${p.name}»?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Producto eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };
  const regen = async (p) => {
    if (p.image_ai_protected) {
      toast.error("Producto protegido. Desbloquéalo desde su ficha para regenerar.");
      return;
    }
    toast.info("Regenerando imagen…");
    try { await api.post(`/admin/products/${p.id}/image/regenerate`); toast.success("Imagen regenerada"); load(); }
    catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "image_protected") {
        toast.error("Producto protegido contra AI");
      } else {
        toast.error("No se pudo regenerar");
      }
    }
  };
  const detectDupes = async () => {
    setDetectingDupes(true);
    const t = toast.loading("IA analizando duplicados…");
    try {
      const { data: r } = await api.post("/admin/ai/products/detect-duplicates", {});
      setDupeGroups(r.groups || []);
      toast.success(`${(r.groups || []).length} grupo(s) sospechosos`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible", { id: t });
    } finally {
      setDetectingDupes(false);
    }
  };
  return (
    <div data-testid="admin-products" className="space-y-4">
      {/* Fase 3 · Catalog kill-switch — sección de visibilidad global. */}
      <AdminCatalogVisibilityCard />
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-3xl font-black tracking-tight flex-1">
          Productos
          {shopFilter && (
            <span className="ml-2 text-sm font-semibold text-neutral-500">
              · {shopFilter === "envio" ? "🇪🇸 Envío" : "🥫 Local"}
            </span>
          )}
        </h1>
        {/* Phase 32 — shop filter dropdown */}
        <Select value={shopFilter || "all"} onValueChange={(v)=>setShopFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="admin-products-shop-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las tiendas</SelectItem>
            <SelectItem value="envio">🇪🇸 Envío</SelectItem>
            <SelectItem value="local">🥫 Local</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Buscar…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} className="w-64" data-testid="admin-products-search" />
        <Button
          size="sm"
          variant="outline"
          data-testid="admin-ai-detect-dupes-btn"
          disabled={detectingDupes}
          onClick={detectDupes}
          className="gap-1 rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a]"
        >
          <Sparkles className={`h-3.5 w-3.5 ${detectingDupes ? "animate-pulse" : ""}`} /> Detectar duplicados
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid="admin-ai-bulk-rewrite-btn"
          onClick={() => setBulkOpen(true)}
          className="gap-1 rounded-full"
        >
          <Wand2 className="h-3.5 w-3.5" /> Reescribir descripciones IA
        </Button>
        <Button asChild className="bg-[#00953B] hover:bg-[#007a30]" data-testid="admin-new-product-btn"><Link to="/admin/productos/nuevo"><Plus className="h-4 w-4 mr-1"/>Nuevo</Link></Button>
      </div>

      {/* Phase 32 — bulk shop actions bar (only when selection is non-empty). */}
      {selected.size > 0 && (
        <div
          data-testid="admin-bulk-actions-bar"
          className="rounded-2xl border border-[#00953B] bg-[#F1FAF3] px-4 py-3 flex flex-wrap items-center gap-2"
        >
          <span className="text-sm font-semibold text-[#00753a] mr-2">
            {selected.size} seleccionado(s):
          </span>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={()=>bulkShop("envio","add")} data-testid="admin-bulk-add-envio" className="rounded-full">
            <Truck className="h-3.5 w-3.5 mr-1" /> Añadir a Envío
          </Button>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={()=>bulkShop("local","add")} data-testid="admin-bulk-add-local" className="rounded-full">
            <StoreIcon className="h-3.5 w-3.5 mr-1" /> Añadir a Local
          </Button>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={()=>bulkShop("envio","remove")} data-testid="admin-bulk-remove-envio" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            Quitar de Envío
          </Button>
          <Button size="sm" variant="outline" disabled={bulkBusy} onClick={()=>bulkShop("local","remove")} data-testid="admin-bulk-remove-local" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            Quitar de Local
          </Button>
          <button
            className="ml-auto text-sm text-neutral-500 hover:text-neutral-800 underline"
            onClick={()=>setSelected(new Set())}
            data-testid="admin-bulk-clear-selection"
          >
            Limpiar selección
          </button>
        </div>
      )}

      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3 w-8">
                <input
                  type="checkbox"
                  data-testid="admin-select-all"
                  checked={data.items.length > 0 && selected.size === data.items.length}
                  onChange={toggleSelectAll}
                  aria-label="Seleccionar todos"
                />
              </th>
              <th>Imagen</th><th>Nombre</th><th>Marca</th><th>Tiendas</th><th>Precio</th><th>Stock</th><th>Nuevo</th><th>Destacado</th><th>🔒</th><th className="text-right pr-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug}`} className="border-t">
                <td className="p-2">
                  <input
                    type="checkbox"
                    data-testid={`admin-select-${p.slug}`}
                    checked={selected.has(p.id)}
                    onChange={()=>toggleSelect(p.id)}
                    aria-label={`Seleccionar ${p.name}`}
                  />
                </td>
                <td className="p-2"><img src={imageUrl(p.image_url)} alt="" className="h-10 w-10 object-contain" /></td>
                <td className="font-medium">{p.name}</td>
                <td>{p.brand}</td>
                <td data-testid={`admin-shops-${p.slug}`}>
                  <div className="flex flex-wrap gap-1">
                    {(p.shops || []).includes("envio") && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-semibold" title="Envío desde España">
                        🇪🇸 Envío
                      </span>
                    )}
                    {(p.shops || []).includes("local") && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold" title="Tiendas locales de los campamentos">
                        🥫 Local
                      </span>
                    )}
                  </div>
                </td>
                <td className="font-semibold whitespace-nowrap">
                  <div className="flex flex-col gap-0.5">
                    {(p.shops || []).includes("envio") && (
                      <span className="text-xs" data-testid={`admin-price-envio-${p.slug}`}>
                        🇪🇸 {formatEUR((p.shop_prices && p.shop_prices.envio) ?? p.price_eur)}
                      </span>
                    )}
                    {(p.shops || []).includes("local") && (
                      <span className="text-xs" data-testid={`admin-price-local-${p.slug}`}>
                        🥫 {formatEUR((p.shop_prices && p.shop_prices.local) ?? p.price_eur)}
                      </span>
                    )}
                  </div>
                </td>
                <td><Switch checked={p.in_stock} onCheckedChange={(v)=>toggle(p.id,'in_stock',v)} data-testid={`admin-stock-${p.slug}`} /></td>
                <td><Switch checked={p.is_new} onCheckedChange={(v)=>toggle(p.id,'is_new',v)} /></td>
                <td><Switch checked={!!p.is_featured} onCheckedChange={(v)=>toggle(p.id,'is_featured',v)} data-testid={`admin-featured-${p.slug}`} /></td>
                <td>
                  <button
                    type="button"
                    onClick={() => toggle(p.id, 'image_ai_protected', !p.image_ai_protected)}
                    data-testid={`admin-protected-${p.slug}`}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-neutral-100 transition-colors"
                    title={p.image_ai_protected ? "Imagen protegida contra AI — clic para desbloquear" : "Clic para proteger contra AI"}
                    aria-label={p.image_ai_protected ? "Desbloquear imagen" : "Proteger imagen"}
                    aria-pressed={!!p.image_ai_protected}
                  >
                    {p.image_ai_protected
                      ? <Lock className="h-4 w-4 text-amber-600" />
                      : <span className="text-neutral-300"><Lock className="h-4 w-4 opacity-40" /></span>}
                  </button>
                </td>
                <td className="text-right pr-3 space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" asChild data-testid={`admin-edit-${p.slug}`}><Link to={`/admin/productos/${p.id}/editar`}>Editar</Link></Button>
                  <Button size="sm" variant="outline" onClick={()=>regen(p)}><RefreshCw className="h-3 w-3"/></Button>
                  <Button size="sm" variant="outline" onClick={()=>del(p)} data-testid={`admin-delete-${p.slug}`}><Trash2 className="h-3 w-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dupeGroups !== null && (
        <DupeGroupsModal groups={dupeGroups} items={data.items} onClose={() => setDupeGroups(null)} onDeleted={load} />
      )}
      {bulkOpen && (
        <BulkRewriteModal onClose={() => setBulkOpen(false)} onDone={load} />
      )}
    </div>
  );
}

function DupeGroupsModal({ groups, items, onClose, onDeleted }) {
  const byId = Object.fromEntries(items.map((p) => [p.id, p]));
  return (
    <div role="dialog" aria-label="Duplicados detectados" data-testid="admin-dupes-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={(e)=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg">Grupos duplicados detectados</h3>
          <button data-testid="admin-dupes-modal-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100">
            <span aria-hidden>×</span>
          </button>
        </div>
        {groups.length === 0 ? (
          <p className="text-sm text-neutral-500">No se han detectado duplicados obvios.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g, i) => (
              <div key={i} data-testid={`admin-dupes-group-${i}`} className="rounded-xl border border-neutral-200 p-3 space-y-2">
                <div className="text-xs text-neutral-500">Cluster {i + 1}</div>
                {g.map((pid) => (
                  <div key={pid} className="flex items-center justify-between text-sm">
                    <span className="truncate">{byId[pid]?.name || pid}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm(`¿Eliminar «${byId[pid]?.name || pid}»?`)) return;
                        try { await api.delete(`/admin/products/${pid}`); toast.success("Eliminado"); onDeleted(); } catch { toast.error("Error"); }
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BulkRewriteModal({ onClose, onDone }) {
  const [cats, setCats] = useState([]);
  const [style, setStyle] = useState("detallado");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [limit, setLimit] = useState(20);
  const [running, setRunning] = useState(false);
  useEffect(() => { api.get("/admin/categories").then((r)=>setCats(r.data)); }, []);
  const parents = cats.filter(c => !c.parent_id);
  const subs = cats.filter(c => c.parent_id === parents.find(p=>p.slug===category)?.id);
  const run = async () => {
    setRunning(true);
    const t = toast.loading("IA reescribiendo descripciones…");
    try {
      const { data } = await api.post("/admin/ai/products/bulk-rewrite-descriptions", {
        category_slug: category || null,
        subcategory_slug: subcategory || null,
        style,
        limit,
      });
      toast.success(`Actualizadas: ${data.updated} · Omitidas: ${data.skipped} · Errores: ${data.errors?.length || 0}`, { id: t, duration: 6000 });
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible", { id: t });
    } finally { setRunning(false); }
  };
  return (
    <div role="dialog" aria-label="Reescribir descripciones" data-testid="admin-bulk-rewrite-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={(e)=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
        <h3 className="font-black text-lg">Reescribir descripciones con IA</h3>
        <div className="grid gap-3">
          <div>
            <Label>Categoría (opcional)</Label>
            <Select value={category} onValueChange={(v)=>{ setCategory(v); setSubcategory(""); }}>
              <SelectTrigger data-testid="admin-bulk-cat"><SelectValue placeholder="Todas"/></SelectTrigger>
              <SelectContent>
                {parents.map(c => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {category && subs.length > 0 && (
            <div>
              <Label>Subcategoría (opcional)</Label>
              <Select value={subcategory} onValueChange={setSubcategory}>
                <SelectTrigger><SelectValue placeholder="Todas"/></SelectTrigger>
                <SelectContent>
                  {subs.map(c => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Estilo</Label>
            <Select value={style} onValueChange={setStyle}>
              <SelectTrigger data-testid="admin-bulk-style"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="corto">Corto</SelectItem>
                <SelectItem value="detallado">Detallado</SelectItem>
                <SelectItem value="ofertas">Ofertas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Máximo por ejecución</Label>
            <Input type="number" value={limit} onChange={(e)=>setLimit(parseInt(e.target.value)||20)} min={1} max={50} data-testid="admin-bulk-limit" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" disabled={running} data-testid="admin-bulk-run" onClick={run} className="bg-[#00953B] hover:bg-[#007a30]">
            <Wand2 className={`h-3.5 w-3.5 mr-1 ${running ? "animate-pulse" : ""}`} /> Ejecutar
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProductEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name:"", brand:"MercaGo", category_id:"", subcategory_id:"", price_eur:0, price_per_unit:"", unit_label:"", description:"", ingredients:"", nutritional_info:{}, in_stock:true, is_new:false, is_featured:false, image_ai_protected:false, image_url:"", shops:["envio"], shop_prices:{} });
  const [aiBusy, setAiBusy] = useState(""); // which AI button is running
  const [aiHint, setAiHint] = useState("");
  // Phase 31 — confirmation dialog before AI overwrites a protected image.
  // `pendingAiAction` is a nullable callback that receives no args and runs
  // the AI request with `?force=true` once the admin confirms.
  const [pendingAiAction, setPendingAiAction] = useState(null);
  useEffect(() => {
    api.get("/admin/categories").then((r)=>setCats(r.data));
    if (!isNew) api.get(`/admin/products?limit=500`).then((r)=>{ const p=r.data.items.find(x=>x.id===id); if(p) setForm(f=>({...f, ...p})); });
  }, [id, isNew]);
  const parents = cats.filter(c=>!c.parent_id);
  const subs = cats.filter(c=>c.parent_id===form.category_id);
  const save = async () => {
    // Phase 32 — validate shop membership + at least one price source.
    const shops = form.shops || [];
    if (shops.length === 0) {
      toast.error("Marca al menos una tienda");
      return;
    }
    const priceForShop = (s) =>
      (form.shop_prices && form.shop_prices[s] != null && form.shop_prices[s] > 0)
        ? form.shop_prices[s]
        : (form.price_eur || 0);
    const missingPrice = shops.find((s) => !(priceForShop(s) > 0));
    if (missingPrice) {
      toast.error(`Falta precio para la tienda ${missingPrice === "envio" ? "Envío" : "Local"} (o rellena el precio base)`);
      return;
    }
    try {
      if (isNew) {
        // Phase 22 — send only real, non-null fields on create so we don't
        // overwrite unset optional keys.
        const payload = { ...form };
        // Empty nutritional_info dict must be null (backend model expects it).
        if (payload.nutritional_info && Object.keys(payload.nutritional_info).length === 0) {
          payload.nutritional_info = null;
        }
        if (payload.subcategory_id === "") payload.subcategory_id = null;
        const {data} = await api.post("/admin/products", payload);
        toast.success("Producto creado");
        navigate(`/admin/productos/${data.id}/editar`, { replace: true });
      } else {
        // Phase 22 — send only the fields we know exist; nutritional_info as null
        // when empty. Then re-fetch so the form mirrors the DB after save.
        const payload = { ...form };
        if (payload.nutritional_info && Object.keys(payload.nutritional_info).length === 0) {
          payload.nutritional_info = null;
        }
        if (payload.subcategory_id === "") payload.subcategory_id = null;
        const {data} = await api.patch(`/admin/products/${id}`, payload);
        // Reflect canonical DB values in the form (server may normalize slug/image_url).
        // Phase 29 bug fix — the backend intentionally strips the cache-bust
        // suffix (`?v=timestamp`) before persisting. If we blindly copied its
        // `image_url` back into the form we would replace our cache-busted URL
        // with a plain one, and the browser would serve the OLD image from its
        // HTTP cache. Preserve our client-side cache-bust when it points at the
        // same canonical path the server just confirmed.
        if (data && typeof data === "object") {
          setForm((f) => {
            const next = { ...f, ...data };
            if (
              f.image_url &&
              data.image_url &&
              f.image_url.split("?")[0] === data.image_url
            ) {
              next.image_url = f.image_url;
            }
            return next;
          });
        }
        toast.success("Producto actualizado");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };
  // Phase 31 — helper: if the current form has `image_ai_protected=true`, pop
  // the confirmation modal and run the AI action (with `?force=true`) only
  // after the admin confirms. Also clears the flag so a follow-up AI action
  // in the same session doesn't re-prompt. When unprotected, runs immediately.
  const runOrConfirmAiOverwrite = (aiActionForced) => {
    if (form.image_ai_protected) {
      setPendingAiAction(() => aiActionForced);
    } else {
      aiActionForced();
    }
  };

  const regen = async () => {
    if (isNew) return toast.error("Guarda primero el producto");
    const doRegen = async () => {
      toast.info("Regenerando…");
      try {
        const {data} = await api.post(
          `/admin/products/${id}/image/regenerate`,
          undefined,
          { params: form.image_ai_protected ? { force: true } : {} }
        );
        // Backend ya devuelve image_url con ?v=<ts>; no encadenar otro query-string.
        setForm(f => ({...f, image_url: data.image_url, image_ai_protected: false}));
        // Persist the unlock so refreshing the page reflects it.
        if (form.image_ai_protected) {
          try {
            await api.patch(`/admin/products/${id}`, { image_ai_protected: false });
          } catch (_) { /* soft-fail: image already regenerated */ }
        }
        toast.success("Imagen lista");
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const isProtected =
          typeof detail === "object" && detail?.error === "image_protected";
        toast.error(
          isProtected
            ? "Producto protegido contra AI. Desbloquéalo primero."
            : detail === "ai_image_temporarily_unavailable"
              ? "El generador de imágenes está saturado. Inténtalo en 30 segundos."
              : "No se pudo regenerar",
        );
      }
    };
    runOrConfirmAiOverwrite(doRegen);
  };
  const upload = async (e) => {
    // Legacy handler kept for compat with any lingering references; the new
    // <ImageDropZone> handles uploads directly. The stray call would only
    // trigger if a hidden `pe-upload` input still exists in a customized fork.
    if (isNew) return toast.error("Guarda primero el producto");
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const {data} = await api.post(`/admin/products/${id}/image/upload`, fd, {headers:{"Content-Type":"multipart/form-data"}}); setForm(x=>({...x,image_url:data.image_url})); toast.success("Imagen subida"); }
    catch { toast.error("Error al subir"); }
  };

  // Phase 21 AI helpers
  const aiAutofill = async () => {
    if (!form.name?.trim()) return toast.error("Escribe primero el nombre");
    setAiBusy("autofill");
    const t = toast.loading("IA rellenando el producto…");
    try {
      const catObj = parents.find(c=>c.id===form.category_id);
      const {data} = await api.post("/admin/ai/product/autofill", {
        name: form.name, category: catObj?.name || null, brand: form.brand || null,
      });
      setForm(f => ({
        ...f,
        description: data.description || f.description,
        ingredients: data.ingredients || f.ingredients,
        nutritional_info: data.nutritional_info || f.nutritional_info,
        unit_label: data.unit_label_suggestion || f.unit_label,
        price_per_unit: data.price_per_unit_suggestion || f.price_per_unit,
        brand: data.brand_suggestion || f.brand,
      }));
      toast.success("Completado por IA", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible", { id: t });
    } finally { setAiBusy(""); }
  };
  const aiRewrite = async (style) => {
    if (!form.name?.trim()) return toast.error("Escribe primero el nombre");
    setAiBusy("rewrite");
    const t = toast.loading("IA reescribiendo…");
    try {
      const {data} = await api.post("/admin/ai/product/rewrite-description", {
        name: form.name, description: form.description || "", style,
      });
      setForm(f=>({...f, description: data.description}));
      toast.success("Descripción actualizada", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible", { id: t });
    } finally { setAiBusy(""); }
  };
  const aiSuggestCategory = async () => {
    if (!form.name?.trim()) return toast.error("Escribe primero el nombre");
    setAiBusy("suggest");
    const t = toast.loading("IA sugiriendo categoría…");
    try {
      const {data} = await api.post("/admin/ai/product/suggest-category", { name: form.name, brand: form.brand });
      const parent = cats.find(c=>c.slug===data.parent_slug);
      const sub = data.subcategory_slug ? cats.find(c=>c.slug===data.subcategory_slug) : null;
      if (parent) setForm(f=>({...f, category_id: parent.id, subcategory_id: sub?.id || ""}));
      toast.success(`Sugerido: ${parent?.name || "?"}${sub? " / "+sub.name : ""} (${Math.round((data.confidence||0)*100)}%)`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible", { id: t });
    } finally { setAiBusy(""); }
  };
  const aiImageWithHint = async () => {
    if (isNew) return toast.error("Guarda primero el producto");
    const doGenerate = async () => {
      setAiBusy("img");
      const t = toast.loading("Generando imagen IA…");
      try {
        const {data} = await api.post(
          "/admin/ai/product/generate-image",
          { product_id: id, prompt_hint: aiHint || "" },
          { params: form.image_ai_protected ? { force: true } : {} },
        );
        // Backend ya devuelve image_url con ?v=<ts>; no encadenar otro query-string.
        setForm(f=>({...f, image_url: data.image_url, image_ai_protected: false}));
        if (form.image_ai_protected) {
          try {
            await api.patch(`/admin/products/${id}`, { image_ai_protected: false });
          } catch (_) { /* soft-fail */ }
        }
        toast.success("Imagen generada", { id: t });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const isProtected =
          typeof detail === "object" && detail?.error === "image_protected";
        toast.error(
          isProtected
            ? "Producto protegido contra AI. Desbloquéalo primero."
            : detail === "ai_image_temporarily_unavailable"
              ? "El generador de imágenes está saturado. Inténtalo en 30 segundos."
              : detail === "ai_timeout"
                ? "IA tardó demasiado"
                : "No se pudo generar",
          { id: t },
        );
      } finally { setAiBusy(""); }
    };
    runOrConfirmAiOverwrite(doGenerate);
  };

  return (
    <div data-testid="admin-product-edit" className="max-w-3xl space-y-4">
      <h1 className="text-3xl font-black tracking-tight">{isNew?"Nuevo producto":"Editar producto"}</h1>
      <div className="grid sm:grid-cols-2 gap-4 rounded-2xl border bg-white p-5">
        <div className="sm:col-span-2 flex flex-wrap gap-2 pb-2 border-b border-neutral-100">
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="admin-ai-autofill-btn"
            disabled={aiBusy==="autofill" || !form.name?.trim()}
            onClick={aiAutofill}
            className="gap-1 rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a]"
          >
            <Sparkles className={`h-3.5 w-3.5 ${aiBusy==="autofill"?"animate-pulse":""}`} />
            Autocompletar con IA
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="admin-ai-suggest-category-btn"
            disabled={aiBusy==="suggest" || !form.name?.trim()}
            onClick={aiSuggestCategory}
            className="gap-1 rounded-full"
          >
            <TagIcon className="h-3.5 w-3.5" /> Sugerir categoría con IA
          </Button>
        </div>
        <div><Label>Nombre</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="pe-name" /></div>
        <div><Label>Marca</Label><Input value={form.brand} onChange={(e)=>setForm({...form,brand:e.target.value})} /></div>
        <div><Label>Categoría</Label>
          <Select value={form.category_id} onValueChange={(v)=>setForm({...form,category_id:v,subcategory_id:""})}>
            <SelectTrigger data-testid="pe-cat"><SelectValue placeholder="Selecciona…"/></SelectTrigger>
            <SelectContent>{parents.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Subcategoría</Label>
          <Select value={form.subcategory_id||""} onValueChange={(v)=>setForm({...form,subcategory_id:v})}>
            <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
            <SelectContent>{subs.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Precio base (€) <span className="text-xs text-neutral-400">— fallback si una tienda no tiene precio propio</span></Label><Input type="number" step="0.01" value={form.price_eur} onChange={(e)=>setForm({...form,price_eur:parseFloat(e.target.value)||0})} data-testid="pe-price" /></div>
        <div><Label>Unidad (ej. «1 L»)</Label><Input value={form.unit_label} onChange={(e)=>setForm({...form,unit_label:e.target.value})} /></div>
        <div><Label>Precio/ud</Label><Input value={form.price_per_unit} onChange={(e)=>setForm({...form,price_per_unit:e.target.value})} /></div>

        {/* Phase 32 — shop membership + per-shop prices. */}
        <div className="sm:col-span-2 border-t border-neutral-100 pt-4 mt-2">
          <Label className="text-sm font-semibold">Disponible en tiendas</Label>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            {[
              { code: "envio", label: "🇪🇸 Comprar en España y enviar", short: "Envío" },
              { code: "local", label: "🥫 Comprar en los campamentos", short: "Local" },
            ].map(({ code, label, short }) => {
              const active = (form.shops || []).includes(code);
              return (
                <div key={code} className={`rounded-xl border p-3 flex flex-col gap-2 ${active ? "border-[#00953B] bg-[#F7FCF9]" : "border-neutral-200 bg-white"}`}>
                  <label className="flex items-center gap-2 text-sm font-semibold">
                    <input
                      type="checkbox"
                      data-testid={`admin-form-shop-${code}`}
                      checked={active}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((f) => {
                          const nextShops = new Set(f.shops || []);
                          if (checked) nextShops.add(code); else nextShops.delete(code);
                          const nextPrices = { ...(f.shop_prices || {}) };
                          if (!checked) delete nextPrices[code];
                          return { ...f, shops: Array.from(nextShops), shop_prices: nextPrices };
                        });
                      }}
                    />
                    {label}
                  </label>
                  {active && (
                    <div className="flex items-center gap-2 pl-6">
                      <Label className="text-xs shrink-0">Precio {short} (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`admin-form-price-${code}`}
                        placeholder={`${form.price_eur || 0}`}
                        value={form.shop_prices?.[code] ?? ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setForm((f) => {
                            const nextPrices = { ...(f.shop_prices || {}) };
                            if (raw === "") delete nextPrices[code];
                            else nextPrices[code] = parseFloat(raw) || 0;
                            return { ...f, shop_prices: nextPrices };
                          });
                        }}
                        className="h-9 text-sm"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Al menos una tienda debe estar marcada. Si dejas el precio en blanco se usa el <em>precio base</em>.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-6">
          <label className="flex items-center gap-2"><Switch checked={form.in_stock} onCheckedChange={(v)=>setForm({...form,in_stock:v})}/>En stock</label>
          <label className="flex items-center gap-2"><Switch checked={form.is_new} onCheckedChange={(v)=>setForm({...form,is_new:v})}/>Novedad</label>
          <label className="flex items-center gap-2"><Switch data-testid="admin-form-featured" checked={!!form.is_featured} onCheckedChange={(v)=>setForm({...form,is_featured:v})}/>⭐ Destacado</label>
          <label className="flex items-center gap-2" title="Bloquea a la IA cualquier cambio de imagen. El upload manual sigue funcionando.">
            <Switch
              data-testid="admin-form-image-protected"
              checked={!!form.image_ai_protected}
              onCheckedChange={async (v) => {
                setForm({ ...form, image_ai_protected: v });
                if (!isNew) {
                  // Persist immediately so the flag is durable even if the
                  // admin closes the page without pressing Guardar.
                  try {
                    await api.patch(`/admin/products/${id}`, {
                      image_ai_protected: v,
                    });
                    toast.success(v ? "Imagen protegida contra IA" : "Protección retirada");
                  } catch (_) { /* soft-fail: form save will retry */ }
                }
              }}
            />
            <Lock className="h-3.5 w-3.5" /> Proteger imagen contra AI
          </label>
        </div>
        <div className="sm:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <Label>Descripción</Label>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="ghost" data-testid="admin-ai-rewrite-btn-corto" disabled={aiBusy==="rewrite"} onClick={()=>aiRewrite("corto")} className="h-7 text-xs text-[#00953B] gap-1">
                <Wand2 className="h-3 w-3" /> Corto
              </Button>
              <Button type="button" size="sm" variant="ghost" data-testid="admin-ai-rewrite-btn" disabled={aiBusy==="rewrite"} onClick={()=>aiRewrite("detallado")} className="h-7 text-xs text-[#00953B] gap-1">
                <Wand2 className="h-3 w-3" /> Detallado
              </Button>
              <Button type="button" size="sm" variant="ghost" data-testid="admin-ai-rewrite-btn-ofertas" disabled={aiBusy==="rewrite"} onClick={()=>aiRewrite("ofertas")} className="h-7 text-xs text-[#00953B] gap-1">
                <Wand2 className="h-3 w-3" /> Ofertas
              </Button>
            </div>
          </div>
          <Input value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} data-testid="pe-description" />
        </div>
        <div className="sm:col-span-2"><Label>Ingredientes</Label><Input value={form.ingredients} onChange={(e)=>setForm({...form,ingredients:e.target.value})} /></div>
      </div>
      {!isNew && (
        <div className="rounded-2xl border bg-white p-5 space-y-3">
          {form.image_ai_protected && (
            <div
              data-testid="admin-form-image-protected-badge"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 text-xs font-semibold"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Imagen protegida contra AI
            </div>
          )}
          <ImageDropZone
            productId={id}
            currentImageUrl={form.image_url}
            onUploaded={(data) => setForm((x) => ({ ...x, image_url: data.image_url }))}
          />
          <div className="pt-3 border-t border-neutral-100 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={regen} variant="outline" data-testid="pe-regen" size="sm" className="gap-1 rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3]">
                <RefreshCw className="h-3.5 w-3.5" />
                Generar con IA
              </Button>
              <span className="text-xs text-neutral-400">o con detalle:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Detalle opcional para IA (ej. 'con etiqueta verde y logo dorado')"
                value={aiHint}
                onChange={(e)=>setAiHint(e.target.value)}
                data-testid="admin-ai-image-hint-input"
                className="h-8 text-xs"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="admin-ai-image-with-hint-btn"
                disabled={aiBusy==="img"}
                onClick={aiImageWithHint}
                className="gap-1 rounded-full whitespace-nowrap border-[#00953B] text-[#00953B]"
              >
                <Sparkles className={`h-3.5 w-3.5 ${aiBusy==="img"?"animate-pulse":""}`} />
                Generar con detalle
              </Button>
            </div>
          </div>
        </div>
      )}
      <Button onClick={save} className="bg-[#00953B] hover:bg-[#007a30]" data-testid="pe-save"><Save className="h-4 w-4 mr-1"/>Guardar</Button>

      {/* Phase 31 — confirmation modal for AI overwrites of a protected image */}
      <AlertDialog
        open={pendingAiAction !== null}
        onOpenChange={(open) => { if (!open) setPendingAiAction(null); }}
      >
        <AlertDialogContent data-testid="admin-ai-protect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Imagen protegida contra AI
            </AlertDialogTitle>
            <AlertDialogDescription>
              Este producto tiene la imagen protegida. ¿Deseas desbloquearla
              y regenerar con AI? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-ai-protect-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-ai-protect-confirm"
              onClick={() => {
                const fn = pendingAiAction;
                setPendingAiAction(null);
                if (fn) fn();
              }}
              className="bg-[#00953B] hover:bg-[#007a30]"
            >
              Desbloquear y regenerar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const load = () => api.get("/admin/categories").then((r)=>setCats(r.data));
  useEffect(() => { load(); }, []);
  const del = async (c) => {
    if (!window.confirm(`Eliminar "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Eliminada"); load(); }
    catch(e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const parents = cats.filter(c=>!c.parent_id);
  return (
    <div data-testid="admin-categories" className="space-y-4">
      <h1 className="text-3xl font-black tracking-tight">Categorías</h1>
      <div className="rounded-2xl border bg-white p-5">
        {parents.map((p) => (
          <div key={p.id} data-testid={`admin-cat-${p.slug}`} className="py-3 border-b last:border-0">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{p.name}</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={()=>del(p)}><Trash2 className="h-3 w-3"/></Button>
              </div>
            </div>
            <div className="mt-2 pl-4 space-y-1 text-sm">
              {cats.filter(c=>c.parent_id===p.id).map(s=>(
                <div key={s.id} className="flex items-center justify-between">
                  <span>· {s.name}</span>
                  <Button size="sm" variant="ghost" onClick={()=>del(s)}><Trash2 className="h-3 w-3"/></Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_OPTIONS = ["pendiente","preparando","enviado","entregado","cancelado","confirmed"];
const ORDER_ASSIGNABLE_STATUSES = new Set(["confirmed", "preparando", "enviado", "entregado"]);

function Orders() {
  const [data, setData] = useState({items:[], total:0});
  const [status, setStatus] = useState("all");
  const [zipBusy, setZipBusy] = useState(false);
  // Phase 43 — transporter column + bulk + filter
  const [transporters, setTransporters] = useState([]);
  const [transporterFilter, setTransporterFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTargets, setAssignTargets] = useState([]);

  const load = () => {
    const parts = ["limit=100"];
    if (status !== "all") parts.push(`status=${status}`);
    if (transporterFilter !== "all") parts.push(`transporter_id=${transporterFilter}`);
    return api.get(`/admin/orders?${parts.join("&")}`).then(r => {
      setData(r.data);
      setSelectedIds(prev => {
        const visible = new Set((r.data.items || []).map(i => i.id));
        return new Set([...prev].filter(id => visible.has(id)));
      });
    });
  };
  useEffect(()=>{load();},[status, transporterFilter]); // eslint-disable-line
  useEffect(() => {
    api.get("/admin/transporters", { params: { active_only: false } })
      .then(({ data }) => setTransporters(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const visibleAssignableIds = React.useMemo(
    () => (data.items || []).filter(o => ORDER_ASSIGNABLE_STATUSES.has(o.status)).map(o => o.id),
    [data.items],
  );
  const allAssignableSelected =
    visibleAssignableIds.length > 0 &&
    visibleAssignableIds.every(id => selectedIds.has(id));

  const toggleRow = (id, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(visibleAssignableIds));
    else setSelectedIds(new Set());
  };
  const openBulkAssign = () => {
    if (selectedIds.size === 0) { toast.error("Selecciona al menos un pedido"); return; }
    setAssignTargets([...selectedIds]);
    setAssignOpen(true);
  };
  const openSingleAssign = (id) => {
    setAssignTargets([id]);
    setAssignOpen(true);
  };

  return (
    <div data-testid="admin-orders" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-3xl font-black tracking-tight flex-1">Pedidos</h1>
        <Button
          data-testid="admin-download-orders-by-status-btn"
          variant="outline"
          onClick={async () => {
            const t = toast.loading("Generando Excel por estado…");
            setZipBusy(true);
            try {
              const today = new Date().toISOString().slice(0, 10);
              const tz = encodeURIComponent(getBrowserTz());
              await downloadAdminPdf(
                `/admin/reports/orders-by-status.zip?tz=${tz}`,
                `pedidos_por_estado_${today}.zip`,
              );
              toast.success("ZIP descargado", { id: t });
            } catch (_e) {
              toast.error("No se pudo generar el ZIP", { id: t });
            } finally {
              setZipBusy(false);
            }
          }}
          disabled={zipBusy}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" />
          {zipBusy ? "Generando…" : "Descargar Excel por estado (ZIP)"}
        </Button>
        <Button
          data-testid="admin-pedidos-pdf-btn"
          variant="outline"
          onClick={() => downloadPendingPdf("pedidos")}
          className="gap-2 border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3] hover:text-[#00753a] rounded-full"
        >
          <FileDown className="h-4 w-4" /> Descargar PDF pendientes
        </Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUS_OPTIONS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={transporterFilter} onValueChange={setTransporterFilter}>
          <SelectTrigger className="w-52" data-testid="admin-order-transporter-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los transportistas</SelectItem>
            <SelectItem value="unassigned">Sin asignar</SelectItem>
            {transporters.map(t => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}{t.is_active === false ? " (inactivo)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {selectedIds.size > 0 && (
        <div
          data-testid="admin-order-bulk-bar"
          className="sticky top-0 z-10 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 flex items-center gap-3"
        >
          <span className="text-sm text-emerald-900 font-semibold">
            {selectedIds.size} {selectedIds.size === 1 ? "pedido" : "pedidos"} seleccionado{selectedIds.size === 1 ? "" : "s"}
          </span>
          <Button
            data-testid="admin-order-bulk-assign-btn"
            onClick={openBulkAssign}
            className="ml-auto gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
          >
            <UserPlus className="h-4 w-4" /> Asignar seleccionados a…
          </Button>
          <Button variant="outline" onClick={() => setSelectedIds(new Set())} data-testid="admin-order-bulk-clear-btn">Limpiar</Button>
        </div>
      )}
      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
            <tr>
              <th className="p-3 w-10">
                <Checkbox
                  checked={allAssignableSelected}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid="admin-order-bulk-checkbox-all"
                  aria-label="Seleccionar todos"
                />
              </th>
              <th className="p-3">Fecha</th><th>Pedido</th><th>Cliente</th><th>Campamento</th><th>Total</th><th>Estado</th><th>Transportista</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(o=>{
              const canAssign = ORDER_ASSIGNABLE_STATUSES.has(o.status);
              return (
              <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-t">
                <td className="p-3">
                  {canAssign ? (
                    <Checkbox
                      checked={selectedIds.has(o.id)}
                      onCheckedChange={(v) => toggleRow(o.id, !!v)}
                      data-testid={`admin-order-bulk-checkbox-${o.id}`}
                    />
                  ) : <span className="text-neutral-300 text-xs">—</span>}
                </td>
                <td className="p-2">{new Date(o.created_at).toLocaleDateString("es-ES")}</td>
                <td className="font-mono">#{o.id.slice(0,8)}</td>
                <td>{o.customer_email}</td>
                <td>{o.camp}</td>
                <td className="font-semibold">{formatEUR(o.total)}</td>
                <td><Badge className="bg-emerald-100 text-emerald-800">{o.status}</Badge></td>
                <td>
                  {o.transporter_name ? (
                    <button
                      type="button"
                      onClick={() => canAssign && openSingleAssign(o.id)}
                      disabled={!canAssign}
                      data-testid={`admin-order-assign-btn-${o.id}`}
                      className={`inline-flex items-center gap-1 text-xs font-semibold ${canAssign ? "text-[#00953B] hover:underline" : "text-neutral-500 cursor-default"}`}
                      title={canAssign ? "Reasignar" : "Estado no editable"}
                    >
                      <Truck className="h-3.5 w-3.5" />{o.transporter_name}
                    </button>
                  ) : canAssign ? (
                    <Button size="sm" variant="outline" data-testid={`admin-order-assign-btn-${o.id}`} onClick={() => openSingleAssign(o.id)} className="gap-1 text-xs">
                      <UserPlus className="h-3.5 w-3.5" /> Asignar
                    </Button>
                  ) : <span className="text-neutral-300">—</span>}
                </td>
                <td><Link to={`/admin/pedidos/${o.id}`} className="text-[#00953B] font-semibold">Ver</Link></td>
              </tr>
              );
            })}
            {data.items.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-neutral-500 text-sm">No hay pedidos con este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <AssignTransporterModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        kind="orders"
        itemIds={assignTargets}
        onAssigned={() => { setSelectedIds(new Set()); load(); }}
      />
    </div>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [waPrompt, setWaPrompt] = useState(null); // { status } opened after status PATCH
  const load = () => api.get(`/admin/orders/${id}`).then(r=>setOrder(r.data));
  useEffect(()=>{load();},[id]); // eslint-disable-line
  const changeStatus = async (v) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, {status:v});
      toast.success("Estado actualizado");
      load();
      // Phase 22 — offer to draft & send a WhatsApp notification to the customer.
      setWaPrompt({ status: v });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const downloadPdf = async () => {
    const resp = await api.get(
      `/orders/${id}/receipt.pdf?tz=${encodeURIComponent(getBrowserTz())}`,
      { responseType: "blob" },
    );
    const url = URL.createObjectURL(new Blob([resp.data], {type:"application/pdf"}));
    const a = document.createElement("a"); a.href=url; a.download=`MercaGo-pedido-${id.slice(0,8)}.pdf`; a.click(); URL.revokeObjectURL(url);
  };
  if (!order) return <div className="h-64 bg-neutral-100 rounded animate-pulse" />;
  return (
    <div data-testid="admin-order-detail" className="space-y-4 max-w-3xl">
      <Link to="/admin/pedidos" className="text-sm text-neutral-500">← Volver</Link>
      <h1 className="text-3xl font-black tracking-tight">Pedido #{order.id.slice(0,8)}</h1>
      <div className="rounded-2xl border bg-white p-5 space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-neutral-500">Estado:</span>
          <Select value={order.status} onValueChange={changeStatus}>
            <SelectTrigger className="w-48" data-testid="admin-order-status-select"><SelectValue/></SelectTrigger>
            <SelectContent>{STATUS_OPTIONS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={downloadPdf} variant="outline" data-testid="admin-order-pdf-btn">Descargar PDF</Button>
        </div>
        <div className="text-sm grid grid-cols-2 gap-2">
          <div><span className="text-neutral-500">Cliente:</span> {order.customer_email}</div>
          <div><span className="text-neutral-500">Campamento:</span> {order.camp}</div>
          <div><span className="text-neutral-500">Daira:</span> {order.daira}</div>
          <div><span className="text-neutral-500">Barrio:</span> {order.barrio}</div>
          <div><span className="text-neutral-500">Franja:</span> {order.delivery_slot}</div>
          <div><span className="text-neutral-500">Origen:</span> {order.shopper_origin}</div>
        </div>
        <div className="border-t pt-3">
          {order.items.map(it=>(
            <div key={it.product_id} className="flex justify-between text-sm py-1">
              <span>{it.name} × {it.quantity}</span><span>{formatEUR(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="border-t pt-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{formatEUR(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Envío</span><span>{formatEUR(order.shipping_cost)}</span></div>
          <div className="flex justify-between font-bold"><span>Total</span><span>{formatEUR(order.total)}</span></div>
        </div>
      </div>
      {waPrompt && (
        <StatusMessageModal
          kind="order"
          entityId={order.id}
          status={waPrompt.status}
          phone={order.sender_phone || order.phone || ""}
          onClose={() => setWaPrompt(null)}
        />
      )}
    </div>
  );
}

function Users() {
  const [data, setData] = useState({items:[], total:0});
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/users?limit=100${q?`&search=${encodeURIComponent(q)}`:""}`).then(r=>setData(r.data));
  useEffect(()=>{load();},[]); // eslint-disable-line
  const flipRole = async (u) => {
    const newRole = u.role==="admin" ? "customer" : "admin";
    try { await api.patch(`/admin/users/${u.id}/role`, {role:newRole}); toast.success("Rol actualizado"); load(); }
    catch(e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div data-testid="admin-users" className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-3xl font-black tracking-tight flex-1">Usuarios</h1>
        <Input placeholder="Buscar…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==='Enter'&&load()} className="w-64" />
      </div>
      <div className="rounded-2xl border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-neutral-500 bg-neutral-50">
            <tr><th className="p-3">Nombre</th><th>Email</th><th>Rol</th><th>Pedidos</th><th>Fecha</th><th></th></tr>
          </thead>
          <tbody>
            {data.items.map(u=>(
              <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t">
                <td className="p-2"><Link to={`/admin/usuarios/${u.id}`} className="text-[#00953B] font-semibold hover:underline" data-testid={`admin-user-name-${u.id}`}>{u.name}</Link></td>
                <td>{u.email}</td>
                <td><Badge className={u.role==="admin"?"bg-[#00953B] text-white":"bg-neutral-200"}>{u.role}</Badge></td>
                <td>{u.orders_count}</td>
                <td>{new Date(u.created_at).toLocaleDateString("es-ES")}</td>
                <td>
                  <Button size="sm" variant="outline" asChild data-testid={`admin-user-view-${u.id}`}>
                    <Link to={`/admin/usuarios/${u.id}`}>Ver</Link>
                  </Button>{" "}
                  <Button size="sm" variant="outline" onClick={()=>flipRole(u)} data-testid={`admin-toggle-role-${u.id}`}>Cambiar rol</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/users/${id}`).then((r) => setUser(r.data)).catch(() => setUser(false));
    api.get(`/admin/orders?limit=50&user_id=${id}`).then((r) => setOrders(r.data.items || [])).catch(() => setOrders([]));
  }, [id]);
  const generateSummary = async () => {
    setAiBusy(true);
    const t = toast.loading("IA generando resumen…");
    try {
      const { data } = await api.post(`/admin/ai/customers/${id}/summary`);
      setAiSummary(data.summary || "");
      setAiStats(data.stats || null);
      toast.success("Resumen listo", { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout" ? "IA tardó demasiado" : "IA no disponible",
        { id: t },
      );
    } finally {
      setAiBusy(false);
    }
  };
  if (user === false) {
    return (
      <div data-testid="admin-user-detail-notfound" className="max-w-3xl">
        <Link to="/admin/usuarios" className="text-sm text-neutral-500">← Volver</Link>
        <h1 className="text-3xl font-black tracking-tight mt-2">Usuario no encontrado</h1>
      </div>
    );
  }
  if (!user) return <div className="h-64 bg-neutral-100 rounded-2xl animate-pulse" />;
  return (
    <div data-testid="admin-user-detail" className="space-y-4 max-w-3xl">
      <Link to="/admin/usuarios" className="text-sm text-neutral-500">← Volver</Link>
      <div className="rounded-2xl border bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="admin-user-detail-name">{user.name}</h1>
            <div className="text-neutral-500 text-sm mt-1">{user.email}</div>
            <Badge className={`mt-2 ${user.role === "admin" ? "bg-[#00953B] text-white" : "bg-neutral-200"}`}>
              {user.role}
            </Badge>
          </div>
          <div className="text-right text-xs text-neutral-500">
            <div>Pedidos: <span className="font-semibold text-neutral-800" data-testid="admin-user-orders-count">{user.orders_count}</span></div>
            <div>Envíos: <span className="font-semibold text-neutral-800" data-testid="admin-user-shipments-count">{user.shipments_count ?? 0}</span></div>
            <div>Alta: {new Date(user.created_at).toLocaleDateString("es-ES")}</div>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border bg-white p-5 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00953B]" />
            <h2 className="text-lg font-black">Resumen con IA</h2>
          </div>
          <Button
            size="sm"
            variant="outline"
            data-testid="admin-user-ai-summary-btn"
            disabled={aiBusy}
            onClick={generateSummary}
            className="gap-1 rounded-full border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3]"
          >
            <Sparkles className={`h-3.5 w-3.5 ${aiBusy ? "animate-pulse" : ""}`} />
            {aiSummary ? "Regenerar" : "Generar resumen"}
          </Button>
        </div>
        {aiSummary ? (
          <>
            <p data-testid="admin-user-ai-summary-text" className="text-sm text-neutral-800 leading-relaxed">
              {aiSummary}
            </p>
            {aiStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-neutral-100" data-testid="admin-user-ai-summary-stats">
                <UserStat label="Pedidos" v={aiStats.orders_total} />
                <UserStat label="Envíos" v={aiStats.shipments_total} />
                <UserStat label="LTV" v={`${aiStats.ltv_eur} €`} />
                <UserStat label="Top categoría" v={aiStats.top_category || "—"} />
              </div>
            )}
            <TranslateButton
              value={aiSummary}
              testid="admin-user-ai-summary-translate"
              label="Traducir resumen"
            />
          </>
        ) : (
          <p className="text-sm text-neutral-500">
            Genera un resumen ejecutivo del cliente usando IA (pedidos, LTV, categoría principal…).
          </p>
        )}
      </div>
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="text-lg font-black mb-3">Últimos pedidos</h2>
        {orders.length === 0 ? (
          <p className="text-sm text-neutral-500">Este cliente aún no tiene pedidos.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-neutral-500">
              <tr><th className="py-2">Fecha</th><th>#</th><th>Total</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {orders.slice(0, 10).map((o) => (
                <tr key={o.id} className="border-t">
                  <td className="py-2">{new Date(o.created_at).toLocaleDateString("es-ES")}</td>
                  <td className="font-mono">#{o.id.slice(0, 8)}</td>
                  <td className="font-semibold">{formatEUR(o.total)}</td>
                  <td><Badge className="bg-emerald-100 text-emerald-800">{o.status}</Badge></td>
                  <td><Link to={`/admin/pedidos/${o.id}`} className="text-[#00953B] font-semibold">Ver</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UserStat({ label, v }) {
  return (
    <div>
      <div className="text-xs text-neutral-500">{label}</div>
      <div className="text-sm font-black">{v}</div>
    </div>
  );
}

export default function AdminRoutes() {
  return (
    <RequireAdmin>
      <Routes>
        <Route element={<AdminShell />}>
          <Route index element={<Dashboard />} />
          <Route path="productos" element={<ProductList />} />
          <Route path="productos/nuevo" element={<ProductEdit />} />
          <Route path="productos/:id/editar" element={<ProductEdit />} />
          <Route path="categorias" element={<Categories />} />
          <Route path="pedidos" element={<Orders />} />
          <Route path="pedidos/:id" element={<OrderDetail />} />
          <Route path="envios" element={<AdminShipments />} />
          <Route path="envios/:id" element={<AdminShipmentDetail />} />
          <Route path="salidas" element={<AdminSalidas />} />
          <Route path="usuarios" element={<Users />} />
          <Route path="usuarios/:id" element={<UserDetail />} />
          <Route path="marketing" element={<AdminMarketing />} />
          <Route path="legal" element={<AdminLegal />} />
          <Route path="faq" element={<AdminFaq />} />
          <Route path="testimonios" element={<AdminTestimonials />} />
          <Route path="transportistas" element={<AdminTransporters />} />
          <Route path="settings/payments" element={<AdminPaymentsSettings />} />
          <Route path="promo-codes" element={<AdminPromoCodes />} />
          <Route path="settings/content" element={<AdminContent />} />
          <Route path="ajustes" element={<AdminAjustes />} />
          <Route path="importar-mercadona" element={<ImportarMercadonaPage />} />
          <Route path="whatsapp" element={<AdminWhatsApp />} />
        </Route>
      </Routes>
    </RequireAdmin>
  );
}
